// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Documents API service - pure async functions for document operations.
 */
import type { NodeApiClient } from '@/app-shell/services'
import axios from 'axios'
import type {
  DocumentListResponse,
  CreateDocumentResponse,
  DocumentUploadParams,
  PresignedUploadResponse,
} from '../types'

/** List documents with optional filtering */
export interface ListDocumentsParams {
  groupId?: string
  slotId?: string
  /** Include pipeline execution status for each document */
  includePipelineStatus?: boolean
}

/**
 * List all documents in a workspace with optional filtering.
 */
export async function listDocuments(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  params?: ListDocumentsParams,
): Promise<DocumentListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.groupId) searchParams.set('group_id', params.groupId)
  if (params?.slotId) searchParams.set('slot_id', params.slotId)
  if (params?.includePipelineStatus) searchParams.set('include_pipeline_status', 'true')

  const query = searchParams.toString()
  const url = `/org/${orgId}/ws/${wsId}/documents${query ? `?${query}` : ''}`
  const res = await apiClient.get<DocumentListResponse>(url)
  return res.data
}

/** Progress callback for uploads */
export type UploadProgressCallback = (progress: number) => void

/**
 * Upload a document via presigned S3 URL.
 *
 * 1. POST /contract-review/documents/upload-url → get presigned PUT URL + document_id
 * 2. PUT file directly to S3 (bypasses API Gateway size limit)
 * 3. POST /documents/complete-upload → create DynamoDB record
 */
export async function uploadDocument(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  params: DocumentUploadParams,
  onProgress?: UploadProgressCallback,
): Promise<CreateDocumentResponse> {
  const file = params.file

  // Step 1: Get presigned URL
  const presignUrl = params.uploadUrlBase ?? `/org/${orgId}/ws/${wsId}/contract-review/documents/upload-url`
  const presignRes = await apiClient.post<PresignedUploadResponse>(
    presignUrl,
    {
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      groupId: params.groupId,
      slotId: params.slotId,
      classification: params.classification,
    },
  )
  const { uploadUrl, documentId, s3Key, tagging, groupId } = presignRes.data

  // Step 2: PUT file directly to S3
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'X-Amz-Tagging': tagging,
    },
    onUploadProgress: onProgress
      ? (event) => {
          const progress = event.total
            ? Math.round((event.loaded * 90) / event.total)
            : 45
          onProgress(progress)
        }
      : undefined,
  })

  // Step 3: Finalize document in DynamoDB
  onProgress?.(95)
  const completeRes = await apiClient.post<CreateDocumentResponse>(
    `/org/${orgId}/ws/${wsId}/documents/complete-upload`,
    {
      documentId,
      s3Key,
      filename: file.name,
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
      groupId,
      slotId: params.slotId,
      classification: params.classification,
    },
  )
  onProgress?.(100)
  return completeRes.data
}

/**
 * Delete a document by ID.
 */
export async function deleteDocument(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  groupId?: string,
): Promise<void> {
  const params = groupId ? `?group_id=${encodeURIComponent(groupId)}` : ''
  await apiClient.delete(`/org/${orgId}/ws/${wsId}/documents/${documentId}${params}`)
}

/**
 * Fetch document PDF as blob (with auth).
 */
export async function fetchDocumentPdf(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  version: number,
  groupId?: string,
): Promise<Blob> {
  const params = groupId ? `?group_id=${encodeURIComponent(groupId)}` : ''
  const url = `/org/${orgId}/ws/${wsId}/documents/${documentId}/versions/${version}/pdf${params}`
  const response = await apiClient.getAxiosInstance().get(url, {
    responseType: 'blob',
  })
  return new Blob([response.data], { type: 'application/pdf' })
}

/** Response model for document text endpoint */
export interface DocumentTextResponse {
  documentId: string
  version: number
  text: string
  pageCount: number
}

/** Response model for document HTML endpoint */
export interface DocumentHtmlResponse {
  documentId: string
  version: number
  html: string
}

/** Entry in the formatting data for a page */
export interface FormattingEntry {
  text: string
  formatting: string[] // e.g., ["bold", "strike", "underline"]
  line_number: number
}

/** Response model for document formatting endpoint */
export interface DocumentFormattingResponse {
  documentId: string
  version: number
  pageCount: number
  hasFormatting: boolean
  /** Raw formatting data keyed by page (e.g., "page_1", "page_2") */
  formatting: Record<string, FormattingEntry[]> | null
}

/** Response from view-url endpoint */
export interface ViewUrlResponse {
  url: string
  expiresIn: number
}

/**
 * Get a presigned URL for viewing a document.
 * Use this for cross-domain document access (e.g., window.open).
 */
export async function getDocumentViewUrl(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  version?: number,
  groupId?: string,
): Promise<string> {
  const query = new URLSearchParams()
  if (version) query.set('version', String(version))
  if (groupId) query.set('group_id', groupId)
  const params = query.toString() ? `?${query}` : ''
  const url = `/org/${orgId}/ws/${wsId}/documents/${documentId}/view-url${params}`
  const res = await apiClient.get<ViewUrlResponse>(url)
  return res.data.url
}

/**
 * Get a presigned URL for viewing a single page PDF.
 * Use this for cross-domain page access (e.g., window.open).
 */
export async function getPageViewUrl(
  apiClient: NodeApiClient,
  orgId: string,
  wsId: string,
  documentId: string,
  pageNumber: number,
  options?: { version?: number; download?: boolean },
): Promise<string> {
  const params = new URLSearchParams()
  if (options?.version) params.set('version', String(options.version))
  if (options?.download) params.set('download', 'true')
  const query = params.toString()
  const url = `/org/${orgId}/ws/${wsId}/documents/${documentId}/pages/${pageNumber}/view-url${query ? `?${query}` : ''}`
  const res = await apiClient.get<ViewUrlResponse>(url)
  return res.data.url
}
